import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  CollectionMetadata,
  CollectionMetadataCreatedEnvelope,
  CollectionMetadataDeletedEnvelope,
  CollectionMetadataUpdatedEnvelope,
  CreateCollectionMetadataApplicationErrors,
  GetCollectionMetadataApplicationErrors,
  ListCollectionMetadataOptions,
  ListCollectionMetadataResponse,
  ReplaceCollectionMetadataOptions,
  UpdateCollectionMetadataApplicationErrors,
  createCollectionMetadata as universalCreateCollectionMetadata,
  deleteCollectionMetadata as universalDeleteCollectionMetadata,
  getCollectionMetadata as universalGetCollectionMetadata,
  listCollectionMetadata as universalListCollectionMetadata,
  replaceCollectionMetadata as universalReplaceCollectionMetadata,
  updateCollectionMetadata as universalUpdateCollectionMetadata,
} from './cloud-autocms-v3-collection-metadata-autocms.universal.js';

export const __metadata = {
  PACKAGE_NAME: '@wix/autocms-collection-metadata-service',
};

export function createCollectionMetadata(
  httpClient: HttpClient
): CreateCollectionMetadataSignature {
  return (collectionMetadata: CollectionMetadata) =>
    universalCreateCollectionMetadata(
      collectionMetadata,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCollectionMetadataSignature {
  /**
   * Creates a new collection metadata instance.
   *
   * In case of an error, this endpoint returns the following codes:
   * - `METADATA_EXISTS`: Metadata already exists for the specified collection.
   * @param - Collection metadata details.
   * @returns Created collection metadata instance.
   */
  (collectionMetadata: CollectionMetadata): Promise<
    NonNullablePaths<
      CollectionMetadata,
      `dataCollectionId` | `namedViews` | `permissionsTemplate`,
      2
    > & {
      __applicationErrorsType?: CreateCollectionMetadataApplicationErrors;
    }
  >;
}

export function getCollectionMetadata(
  httpClient: HttpClient
): GetCollectionMetadataSignature {
  return (dataCollectionId: string) =>
    universalGetCollectionMetadata(
      dataCollectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCollectionMetadataSignature {
  /**
   * Gets a collection metadata instance.
   *
   * In case of an error, this endpoint returns the following codes:
   * - `ITEM_NOT_FOUND`: No metadata exists for the specified collection.
   * @param - ID of the collection for which to retrieve metadata.
   * @returns Retrieved collection metadata instance.
   */
  (dataCollectionId: string): Promise<
    NonNullablePaths<
      CollectionMetadata,
      `dataCollectionId` | `namedViews` | `permissionsTemplate`,
      2
    > & {
      __applicationErrorsType?: GetCollectionMetadataApplicationErrors;
    }
  >;
}

export function updateCollectionMetadata(
  httpClient: HttpClient
): UpdateCollectionMetadataSignature {
  return (dataCollectionId: string, collectionMetadata: CollectionMetadata) =>
    universalUpdateCollectionMetadata(
      dataCollectionId,
      collectionMetadata,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCollectionMetadataSignature {
  /**
   * Updates the metadata for the specified collection.
   *
   * In case of an error, this endpoint returns the following codes:
   * - `ITEM_NOT_FOUND`: No metadata exists for the specified collection.
   * @param - TODO CLARIFY WHAT THIS MEANS:
   * ID of metadata to update, ignored if **collection_metadata.data_collection_id** is non-empty // a technical workaround
   * exists because of docs generation issue with nested fields and ** path variables
   * TODO shouldn't this be required? // Yes
   * ID of the collection for which to update metadata.
   * @param - Collection metadata to update. Only the specified fields are affected. Fields not mentioned remain unchanged.
   * @returns Updated collection metadata instance.
   */
  (dataCollectionId: string, collectionMetadata: CollectionMetadata): Promise<
    NonNullablePaths<
      CollectionMetadata,
      `dataCollectionId` | `namedViews` | `permissionsTemplate`,
      2
    > & {
      __applicationErrorsType?: UpdateCollectionMetadataApplicationErrors;
    }
  >;
}

export function replaceCollectionMetadata(
  httpClient: HttpClient
): ReplaceCollectionMetadataSignature {
  return (options?: ReplaceCollectionMetadataOptions) =>
    universalReplaceCollectionMetadata(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ReplaceCollectionMetadataSignature {
  /**
   * Completely replaces all existing collection metadata with the provided metadata.
   */
  (options?: ReplaceCollectionMetadataOptions): Promise<void>;
}

export function deleteCollectionMetadata(
  httpClient: HttpClient
): DeleteCollectionMetadataSignature {
  return (dataCollectionId: string) =>
    universalDeleteCollectionMetadata(
      dataCollectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCollectionMetadataSignature {
  /**
   * Deletes a collection metadata instance. (1)
   * Deletes the specified collection's metadata. (2)
   * @param - ID of the collection for which to delete metadata.
   */
  (dataCollectionId: string): Promise<void>;
}

export function listCollectionMetadata(
  httpClient: HttpClient
): ListCollectionMetadataSignature {
  return (options?: ListCollectionMetadataOptions) =>
    universalListCollectionMetadata(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCollectionMetadataSignature {
  /**
   * Lists all collection metadata.
   */
  (options?: ListCollectionMetadataOptions): Promise<
    NonNullablePaths<
      ListCollectionMetadataResponse,
      | `items`
      | `items.${number}.dataCollectionId`
      | `items.${number}.permissionsTemplate`,
      4
    >
  >;
}

export const onCollectionMetadataCreated = EventDefinition(
  'wix.cloud.autocms.v3.collection_metadata_created',
  true,
  (event: CollectionMetadataCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<CollectionMetadataCreatedEnvelope>();
export const onCollectionMetadataDeleted = EventDefinition(
  'wix.cloud.autocms.v3.collection_metadata_deleted',
  true,
  (event: CollectionMetadataDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<CollectionMetadataDeletedEnvelope>();
export const onCollectionMetadataUpdated = EventDefinition(
  'wix.cloud.autocms.v3.collection_metadata_updated',
  true,
  (event: CollectionMetadataUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<CollectionMetadataUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Asset,
  BaseEventMetadata,
  CollectionMetadata,
  CollectionMetadataCreatedEnvelope,
  CollectionMetadataDeletedEnvelope,
  CollectionMetadataUpdatedEnvelope,
  CreateCollectionMetadataRequest,
  CreateCollectionMetadataResponse,
  CursorPaging,
  CursorPagingMetadata,
  Cursors,
  DeleteCollectionMetadataRequest,
  DeleteCollectionMetadataResponse,
  DeleteContext,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetCollectionMetadataRequest,
  GetCollectionMetadataResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListCollectionMetadataOptions,
  ListCollectionMetadataRequest,
  ListCollectionMetadataResponse,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  PermissionsTemplateType,
  PicassoAssigned,
  PicassoUnassigned,
  ReplaceCollectionMetadataOptions,
  ReplaceCollectionMetadataRequest,
  ReplaceCollectionMetadataResponse,
  RestoreInfo,
  ServiceProvisioned,
  ServiceRemoved,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  UpdateCollectionMetadataRequest,
  UpdateCollectionMetadataResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './cloud-autocms-v3-collection-metadata-autocms.universal.js';
